import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  NotificationState,
  Notification,
  ChatEntityInformationModel,
  BellNotifications,
  MessagesNotifications,
} from 'models';
import { NotificationType, NotificationEntities } from 'utils/enums';
import services from 'services';

const fetchUserSpacesNotifications = async (
  coopId: string
): Promise<Notification[]> => {
  const res = await services.get(
    `/api/user/cooperatives/${coopId}/spaces/notifications`
  );

  const data = res.data.Data;

  return data && Array.isArray(data) ? (data as Notification[]) : [];
};

const fetchChatsNotifications = async (
  coopId: string,
  entities: string[]
): Promise<ChatEntityInformationModel[]> => {
  const url = new URL(
    `api/user/cooperatives/${coopId}/chats/notread`,
    window.location.origin
  );

  entities.forEach((entity) => url.searchParams.append('entityName', entity));

  const res = await services.get(url.toString());

  const data = res.data.Data;

  return data && Array.isArray(data)
    ? (data as ChatEntityInformationModel[])
    : [];
};

const fetchBellNotification = async (
  _coopId: string
): Promise<BellNotifications> => {
  const res = await services.get(`/api/user/notifications/count`);

  const data: BellNotifications = res.data.Data;

  return data;
};

const fetchMessagesNotifications = async (coopId: string) => {
  const res = await services.get(
    `/api/user/cooperatives/${coopId}/events/count`
  );

  return res.data.Data as MessagesNotifications;
};

const fetchBoardMessagesNotifications = async (coopId: string) => {
  const res = await services.get(
    `/api/cooperatives/${coopId}/chats/eventsinformation/notread`
  );

  return res.data.Data as ChatEntityInformationModel[];
};

interface Args {
  coopId: string;
  notificationTypes?: NotificationType[];
  entities?: NotificationEntities[];
}

export const changeNotifications = createAsyncThunk(
  'notifications/changeNotifications',
  async (coopId: string) => await fetchBellNotification(coopId)
);

export const changeMessages = createAsyncThunk(
  'notifications/changeMessages',
  async (coopId: string) => await fetchMessagesNotifications(coopId)
);

export const changeBoardMessages = createAsyncThunk(
  'notifications/changeBoardMessages',
  async (coopId: string) => await fetchBoardMessagesNotifications(coopId)
);

export const changeChatMessage = createAsyncThunk(
  'notifications/changeChatMessage',
  async (options: { coopId: string; entities: NotificationEntities[] }) => {
    const chatMessages = await fetchChatsNotifications(
      options.coopId,
      options.entities
    );

    return { chatMessages, entities: options.entities };
  }
);

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (options: Args) => {
    const {
      coopId,
      notificationTypes = [
        NotificationType.Bell,
        NotificationType.Chat,
        NotificationType.UserSpace,
        NotificationType.Messages,
        NotificationType.BoardMessages,
      ],
      entities = [
        NotificationEntities.EventInformation,
        NotificationEntities.Incident,
        NotificationEntities.Task,
      ],
    } = options;

    return await Promise.allSettled([
      notificationTypes.includes(NotificationType.UserSpace)
        ? fetchUserSpacesNotifications(coopId)
        : Promise.resolve(undefined),
      notificationTypes.includes(NotificationType.Chat)
        ? fetchChatsNotifications(coopId, entities)
        : Promise.resolve(undefined),

      notificationTypes.includes(NotificationType.Bell)
        ? fetchBellNotification(coopId)
        : Promise.resolve(undefined),
      notificationTypes.includes(NotificationType.Messages)
        ? fetchMessagesNotifications(coopId)
        : Promise.resolve(undefined),
      notificationTypes.includes(NotificationType.BoardMessages)
        ? fetchBoardMessagesNotifications(coopId)
        : Promise.resolve(undefined),
    ]);
  }
);

const initialState: NotificationState = {
  user_spaces: {
    amount: 0,
    info: [],
  },
  incident: {
    amount: 0,
    info: [],
  },
  key_order: {
    amount: 0,
    info: [],
  },
  notifications: {
    amount: 0,
    expirationDate: null,
  },
  repair: {
    amount: 0,
    info: [],
  },
  eventsinformation: {
    amount: 0,
    info: [],
  },
  messages: {
    amount: 0,
  },
  board_eventsinformation: {
    amount: 0,
    info: [],
  },
  task: {
    amount: 0,
    info: [],
  },
};

const countChatNotifications = (entities: ChatEntityInformationModel[]) => {
  return entities.reduce((acc, next) => {
    return next.NewMessages ? acc + next.NewMessages : acc;
  }, 0);
};

const buildChatEntities = (
  entities: ChatEntityInformationModel[] | null,
  entity: ChatEntityInformationModel
) => {
  return entities ? entities.concat(entity) : [entity];
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(changeNotifications.fulfilled, (state, action) => {
      const bellNotifications = action.payload;

      state.notifications.amount = bellNotifications.TotalCount || 0;
      state.notifications.expirationDate = bellNotifications.MinDueDate ?? null;
    });
    builder.addCase(changeMessages.fulfilled, (state, action) => {
      const messagesNotifications = action.payload;

      state.messages.amount = messagesNotifications.UserCount || 0;
    });
    builder.addCase(changeBoardMessages.fulfilled, (state, action) => {
      const boardMessageNotifications = action.payload;

      state.board_eventsinformation = {
        info: boardMessageNotifications,
        amount: countChatNotifications(boardMessageNotifications),
      };
    });
    builder.addCase(changeChatMessage.fulfilled, (state, action) => {
      const { chatMessages, entities } = action.payload;

      let keyorder: ChatEntityInformationModel[] | null = null,
        incident: ChatEntityInformationModel[] | null = null,
        repair: ChatEntityInformationModel[] | null = null,
        eventsinformation: ChatEntityInformationModel[] | null = null,
        task: ChatEntityInformationModel[] | null = null;

      for (let item of chatMessages) {
        switch (item.Type) {
          case 'eventsinformation':
            eventsinformation = buildChatEntities(eventsinformation, item);
            break;
          case 'incident':
            incident = buildChatEntities(incident, item);
            break;
          case 'task':
            task = buildChatEntities(task, item);
            break;
          case 'repair':
            repair = buildChatEntities(repair, item);
            break;
          case 'key-order':
            keyorder = buildChatEntities(keyorder, item);
            break;
        }
      }

      if (entities.length === 1) {
        return {
          ...state,
          incident: incident
            ? {
                amount: countChatNotifications(incident),
                info: incident,
              }
            : {
                amount: 0,
                info: [],
              },
          key_order: keyorder
            ? {
                amount: countChatNotifications(keyorder),
                info: keyorder,
              }
            : {
                amount: 0,
                info: [],
              },
          repair: repair
            ? {
                amount: countChatNotifications(repair),
                info: repair,
              }
            : {
                amount: 0,
                info: [],
              },
          eventsinformation: eventsinformation
            ? {
                amount: countChatNotifications(eventsinformation),
                info: eventsinformation,
              }
            : {
                amount: 0,
                info: [],
              },
          task: task
            ? { amount: countChatNotifications(task), info: task }
            : { amount: 0, info: [] },
        };
      }
      return {
        ...state,
        incident: incident
          ? {
              amount: countChatNotifications(incident),
              info: incident,
            }
          : state.incident,
        key_order: keyorder
          ? {
              amount: countChatNotifications(keyorder),
              info: keyorder,
            }
          : state.key_order,
        repair: repair
          ? {
              amount: countChatNotifications(repair),
              info: repair,
            }
          : state.repair,
        eventsinformation: eventsinformation
          ? {
              amount: countChatNotifications(eventsinformation),
              info: eventsinformation,
            }
          : state.eventsinformation,
        task: task
          ? { amount: countChatNotifications(task), info: task }
          : state.task,
      };
    });
    builder.addCase(fetchNotifications.pending, () => {
      return {
        user_spaces: {
          amount: 0,
          info: [],
        },
        incident: {
          amount: 0,
          info: [],
        },
        key_order: {
          amount: 0,
          info: [],
        },
        notifications: {
          amount: 0,
          expirationDate: null,
        },
        repair: {
          amount: 0,
          info: [],
        },
        eventsinformation: {
          amount: 0,
          info: [],
        },
        messages: {
          amount: 0,
        },
        board_eventsinformation: {
          amount: 0,
          info: [],
        },
        task: {
          amount: 0,
          info: [],
        },
      };
    });
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      const [
        userSpacesNotifications,
        chatsNotifications,
        bellNotifications,
        messagesNotifications,
        boardMessagesNotifications,
      ] = action.payload;
      let keyorder: ChatEntityInformationModel[] | null = null,
        incident: ChatEntityInformationModel[] | null = null,
        task: ChatEntityInformationModel[] | null = null,
        repair: ChatEntityInformationModel[] | null = null,
        eventsinformation: ChatEntityInformationModel[] | null = null,
        boardEventsInformation: ChatEntityInformationModel[] | null = null,
        userSpaces: Notification[] | null = null,
        bellNotificationsValue: BellNotifications | null = null,
        messagesNotificationsAmount: number | null = null;

      if (userSpacesNotifications.status === 'fulfilled') {
        const { value } = userSpacesNotifications;

        if (value) {
          userSpaces = value;
        }
      }

      if (chatsNotifications.status === 'fulfilled') {
        const { value } = chatsNotifications;

        if (value) {
          for (let item of value) {
            switch (item.Type) {
              case 'eventsinformation':
                eventsinformation = buildChatEntities(eventsinformation, item);
                break;
              case 'incident':
                incident = buildChatEntities(incident, item);
                break;
              case 'repair':
                repair = buildChatEntities(repair, item);
                break;
              case 'key-order':
                keyorder = buildChatEntities(keyorder, item);
                break;
              case 'task':
                task = buildChatEntities(task, item);
                break;
            }
          }
        }
      }

      if (bellNotifications.status === 'fulfilled') {
        const { value } = bellNotifications;

        if (value) {
          bellNotificationsValue = value;
        }
      }

      if (messagesNotifications.status === 'fulfilled') {
        const { value } = messagesNotifications;

        if (value) {
          messagesNotificationsAmount = value.UserCount || 0;
        }
      }

      if (boardMessagesNotifications.status === 'fulfilled') {
        const { value } = boardMessagesNotifications;

        if (value) {
          for (let item of value) {
            boardEventsInformation = buildChatEntities(
              boardEventsInformation,
              item
            );
          }
        }
      }

      return {
        ...state,
        incident: incident
          ? {
              amount: countChatNotifications(incident),
              info: incident,
            }
          : state.incident,
        key_order: keyorder
          ? {
              amount: countChatNotifications(keyorder),
              info: keyorder,
            }
          : state.key_order,
        notifications: bellNotificationsValue
          ? {
              amount: bellNotificationsValue.TotalCount ?? 0,
              expirationDate: bellNotificationsValue.MinDueDate ?? null,
            }
          : state.notifications,
        messages:
          typeof messagesNotificationsAmount === 'number'
            ? {
                amount: messagesNotificationsAmount,
              }
            : state.messages,
        board_eventsinformation: boardEventsInformation
          ? {
              amount: countChatNotifications(boardEventsInformation),
              info: boardEventsInformation,
            }
          : state.board_eventsinformation,
        repair: repair
          ? {
              amount: countChatNotifications(repair),
              info: repair,
            }
          : state.repair,
        eventsinformation: eventsinformation
          ? {
              amount: countChatNotifications(eventsinformation),
              info: eventsinformation,
            }
          : state.eventsinformation,
        task: task
          ? { amount: countChatNotifications(task), info: task }
          : state.task,
        user_spaces: userSpaces
          ? {
              amount: userSpaces.length,
              info: userSpaces,
            }
          : state.user_spaces,
      };
    });
  },
});

export const { reducer, actions } = notificationsSlice;
